/* eslint-disable no-shadow */
import Vue from 'vue';
import {
  USER_REQUEST,
  USER_ERROR,
  USER_SUCCESS,
  USER_SET_PROFILE_PHOTO,
  USER_SET_AVAILABLE_SECTIONS,
  USER_SET_HAS_SETTINGS_ERRORS,
  USER_HIDE_WORK_ON_PC_MOBILE_POPUP,
  USER_SET_IMPORTANT_NOTIFICATION,
} from '@/store/actions/user';
import { AUTH_LOGOUT } from '@/store/actions/auth';

/**
 * @description Тип аутентификации сотрудника клиники.
 * @type {string}
 */
export const CLINIC_AUTH = 'clinic';

const state = {
  available_sections: [],
  profile: {},
  status: '',
  has_settings_errors: false,
  importantNotification: '',
};

const getters = {
  authOf: (state) => {
    if (state.profile?.name) {
      return CLINIC_AUTH;
    }
    return null;
  },
  getAvailableSections: (state) => state.available_sections,
  getProfile: (state) => state.profile,
  getImportantNotification: (state) => state.importantNotification,
  /**
   * @description Имеет доступные раздел кассы для провайдера "Онлайн-Чеки".
   * @returns bool
   */
  hasCashboxOnlineCheckSection: (state) => state.available_sections.includes('cashbox'),
  hasSettingsErrors: (state) => state.has_settings_errors,
  isProfileLoaded: (state) => !!state.profile.id,
  isSectionAvailable: (state) => (needSection) => state
    .available_sections
    .find((section) => section === needSection),
  /**
   * @description Нуждается в пошаговой инструкции.
   * @returns {boolean}
   */
  needsInstruction: (state) => Boolean(state.profile?.needs_instruction),
  /**
   * @description Показывать всплывающее окно удобства работы с пк.
   * @returns {boolean}
   */
  shownWorkOnPCMobilePopup: (state) => Boolean(state.profile?.shown_work_on_pc_mobile_popup),
  /**
   * @description Получить роль пользователя.
   * @returns {string}
   */
  getUserRole: (state) => state.profile?.role_name,
  /**
   * @description Получить основные данные пользователя.
   * @returns {string}
   */
  getUserBasicData: () => state.profile?.basic_data,
  /**
   * @description Получить идентификатор сотрудника пользователя.
   * @returns {number}
   */
  getUserEmployeeId: () => state.profile?.employee_id,
  /**
   * @description Владелец.
   * @returns {boolean}
   */
  isOwner: () => state.profile?.is_owner ?? false,
};

const actions = {
  [USER_REQUEST]: ({ commit }) => {
    commit(USER_REQUEST);
    return Vue
      .axios
      .get('/api/profile')
      .then(async ({ data }) => {
        const profile = { ...data };
        profile.profile.initials = [data.profile.name, data.profile.surname]
          .reduce((initials, part) => initials + part.charAt(0), '');
        commit(USER_SUCCESS, data.profile);
        commit(USER_SET_AVAILABLE_SECTIONS, data.sections);
        commit(USER_SET_IMPORTANT_NOTIFICATION, data.important_notification);
        return data;
      }, (error) => {
        commit(USER_ERROR);
        throw error;
      });
  },
  [USER_SET_PROFILE_PHOTO]: ({ commit }, url) => {
    commit(USER_SET_PROFILE_PHOTO, url);
  },
  [USER_SET_AVAILABLE_SECTIONS]: ({ commit }, sections) => {
    commit(USER_SET_AVAILABLE_SECTIONS, sections);
  },
  [USER_SET_HAS_SETTINGS_ERRORS]: ({ commit }, hasSettingsErrors) => {
    commit(USER_SET_HAS_SETTINGS_ERRORS, hasSettingsErrors);
  },
  [USER_HIDE_WORK_ON_PC_MOBILE_POPUP]: ({ commit }) => {
    commit(USER_HIDE_WORK_ON_PC_MOBILE_POPUP);
  },
};

const mutations = {
  [USER_REQUEST]: (state) => {
    state.status = 'loading';
  },
  [USER_SET_PROFILE_PHOTO]: (state, url) => {
    state.profile.photo_url = url;
  },
  [USER_SET_AVAILABLE_SECTIONS]: (state, sections) => {
    state.available_sections = sections;
  },
  [USER_SET_HAS_SETTINGS_ERRORS]: (state, hasErrors) => {
    state.has_settings_errors = hasErrors;
  },
  [USER_SUCCESS]: (state, resp) => {
    state.status = 'success';
    Vue.set(state, 'profile', resp);
  },
  [USER_ERROR]: (state) => {
    state.status = 'error';
  },
  [AUTH_LOGOUT]: (state) => {
    state.profile = {};
  },
  [USER_HIDE_WORK_ON_PC_MOBILE_POPUP]: (state) => {
    state.profile.shown_work_on_pc_mobile_popup = false;
  },
  [USER_SET_IMPORTANT_NOTIFICATION]: (state, notification) => {
    state.importantNotification = notification;
  },
};

export default () => ({
  state,
  getters,
  actions,
  mutations,
});
